import * as styledElements from "./OrderBoardStyles";
import FileInput from "../common/FileInput";
import Button from "../common/Button";
import { ButtonTypes } from "../common/Button/constants";
import React from "react";
import { Formik } from "formik";
import { CADSources } from "../../reducers/uploadedBoard";
import {
  UPLOAD_MODES_DATA,
  UploadedFile,
  UploadFormValues,
} from "./UploadForm";
import GerberFileIdentifier, {
  GERBER_FILE_LAYER_MAPPING_SCHEMA,
} from "./GerberFileIdentifier";
import useReuploadGerbers from "../../utils/hooks/commonly-stored/uploaded-board/reuploadGerbers";
import LoadingOverlay from "../LoadingOverlay";
import * as Yup from "yup";
import ErrorComponent from "../ErrorComponent";

export const gerberUploadSchema = Yup.object().shape({
  cadSource: Yup.string().oneOf(Object.values(CADSources)).required(),
  files: Yup.array()
    .of(Yup.array().of(Yup.mixed()).required())
    .required()
    .test(
      "Files are valid",
      "Please select all necessary files.",
      (value, { parent }: { parent: UploadFormValues }) => {
        const requiredFiles =
          UPLOAD_MODES_DATA[parent.cadSource]!.requiredFiles;
        return requiredFiles.every((file, index) => {
          return file.multiple
            ? (value[index]?.length ?? 0) >= 1
            : value[index]?.length === 1;
        });
      },
    ),
  gerberFileLayerMapping: GERBER_FILE_LAYER_MAPPING_SCHEMA.nullable(),
});

export default function ReUploadGerbersForm({
  boardId,
  closeModal,
}: {
  boardId: string;
  closeModal: () => void;
}) {
  const { onSubmit, loading, errored } = useReuploadGerbers(boardId);
  const submitGerbers = (values: UploadFormValues) => {
    onSubmit(values);
    closeModal();
  };

  if (errored) {
    return <ErrorComponent />;
  }

  return (
    <>
      <LoadingOverlay loading={loading} text={"Uploading Gerbers..."} />
      <Formik
        initialValues={{
          cadSource: CADSources.GERBER,
          gerberFileLayerMapping: null,
          bomFileInfo: null,
          pnpFileInfo: null,
          assemblyDrawing: null,
          fabDrawing: null,
          referenceFile: null,
          files: Array(
            UPLOAD_MODES_DATA[CADSources.GERBER]!.requiredFiles.length,
          ).fill([]),
        }}
        onSubmit={submitGerbers}
        validationSchema={gerberUploadSchema}
      >
        {({ handleSubmit, values, setValues, isValid }) => {
          return (
            <styledElements.Form onSubmit={handleSubmit}>
              <styledElements.Container>
                {UPLOAD_MODES_DATA[CADSources.GERBER]!.requiredFiles.map(
                  (requiredFile: UploadedFile, requiredFileIndex) => {
                    if (requiredFileIndex > values.files.length - 1) {
                      // There could be a mismatch due to the asynchronous nature of react effects
                      return null;
                    }
                    return (
                      <FileInput
                        disabled={loading}
                        key={requiredFile.extension}
                        fileExtensions={[requiredFile.extension]}
                        files={values.files[requiredFileIndex]}
                        setFiles={(newFiles: File[]) => {
                          void setValues({
                            ...values,
                            files: values.files.map(
                              (existingFiles, fileIndex) =>
                                fileIndex == requiredFileIndex
                                  ? newFiles
                                  : existingFiles,
                            ),
                          });
                        }}
                        isMultiple={requiredFile.multiple}
                      />
                    );
                  },
                )}
              </styledElements.Container>
              <GerberFileIdentifier file={values.files[0][0]} gerbersOnly />
              <Button
                type={ButtonTypes.SUBMIT}
                label={loading ? "Uploading Files" : "Submit"}
                disabled={!isValid || loading}
                {...(!isValid && !loading
                  ? {
                      leftIconProps: {
                        src: "/icons/locked.svg",
                        alt: "locked",
                        iconDimension: 24,
                      },
                    }
                  : {})}
                centerText
              />
            </styledElements.Form>
          );
        }}
      </Formik>
    </>
  );
}
